/*
 * Figma `124:8` — SA3-01 · Platform Overview · Decision Center.
 *
 * `DashboardTemplate`: the metric strip, then what needs doing beside the chart rather than under
 * it — the chart is context for a decision, the action list *is* the decision.
 *
 * The decision rail is the reason this screen is worth rebuilding. The shipped overview draws
 * health as a wall of service rows and provider cards and leaves the ranking to the reader; at
 * 03:00 during a sale that ranking is the only thing anyone wants. `decisions()` derives it from
 * signals that already exist — a core service down, a provider that cannot send, slips that
 * exhausted automatic retry — and orders them by what they cost.
 *
 * Nothing here is invented. Every row of the rail names the field it came from, and a platform with
 * nothing wrong says so rather than manufacturing a task to fill the panel.
 */
import { useState } from 'react';
import { Alert, BarChart, Button, Card, DashboardTemplate, KpiCard, MeterRow, StatusBadge } from '../..';
import type { BadgeTone } from '../..';
import type { Destination } from '../../destinations';
import type { PlatformOverviewDto, ProviderId, ProviderStatusDto, TenantSummaryDto } from '../../../api';

export type DecisionSeverity = 'critical' | 'high' | 'monitor';

export interface PlatformDecision {
  key: string;
  severity: DecisionSeverity;
  title: string;
  detail: string;
}

const SEVERITY_TONE: Record<DecisionSeverity, BadgeTone> = {
  critical: 'danger', high: 'warning', monitor: 'neutral',
};
const SEVERITY_LABEL: Record<DecisionSeverity, string> = {
  critical: 'CRITICAL', high: 'HIGH', monitor: 'MONITOR',
};
const RANK: Record<DecisionSeverity, number> = { critical: 0, high: 1, monitor: 2 };

/** What needs a person, ordered by what it costs if nobody looks. */
export function decisions(overview: PlatformOverviewDto | null): PlatformDecision[] {
  if (!overview) return [];
  const out: PlatformDecision[] = [];

  const stuck = overview.slipVerificationDeadLetterCount ?? 0;
  if (stuck > 0) {
    out.push({
      key: 'slip-dead-letter',
      severity: 'critical',
      title: `สลิป ${stuck.toLocaleString('th-TH')} ใบค้างรอคนตรวจ`,
      detail: 'ตรวจอัตโนมัติหมดรอบ retry แล้ว — ผู้ซื้อเหล่านี้ยังไม่ได้บัตรจนกว่าจะมีคนดู',
    });
  }

  for (const service of overview.coreServices) {
    if (service.status === 'operational') continue;
    out.push({
      key: `service-${service.name}`,
      severity: service.status === 'down' ? 'critical' : 'high',
      title: `${service.name} · ${service.status === 'down' ? 'ใช้งานไม่ได้' : 'ทำงานช้าผิดปกติ'}`,
      detail: service.detail || (service.latencyMs !== null ? `latency ${service.latencyMs} ms` : 'ไม่มีรายละเอียดเพิ่มเติม'),
    });
  }

  for (const provider of overview.providers) {
    if (provider.availability === 'operational' || provider.availability === 'disabled') continue;
    out.push({
      key: `provider-${provider.id}`,
      severity: provider.availability === 'down' ? 'critical' : 'high',
      title: `${provider.name} · ${provider.availability === 'unconfigured' ? 'ยังไม่ได้ตั้งค่า' : provider.availability === 'down' ? 'ใช้งานไม่ได้' : 'ทำงานได้บางส่วน'}`,
      detail: `${provider.purpose} — ${provider.detail || 'ไม่มีรายละเอียดเพิ่มเติม'}`,
    });
  }

  // Capacity is a monitor rather than an alarm: running low is a thing to plan around, and an
  // alarm that fires a week early is an alarm people learn to ignore.
  for (const provider of overview.providers) {
    // `capacity` is null for a provider that does not meter — nothing to watch, not a zero.
    if (!provider.capacity) continue;
    const { remaining, used, unit } = provider.capacity;
    if (remaining === null || used === null) continue;
    const total = remaining + used;
    if (total <= 0 || remaining / total > 0.2) continue;
    out.push({
      key: `capacity-${provider.id}`,
      severity: 'monitor',
      title: `${provider.name} เหลือโควตา ${remaining.toLocaleString('th-TH')} ${unit === 'credits' ? 'เครดิต' : 'ครั้ง'}`,
      detail: `ใช้ไปแล้ว ${used.toLocaleString('th-TH')} จาก ${total.toLocaleString('th-TH')} — เติมก่อนจะหมดระหว่างรอบขาย`,
    });
  }

  return out.sort((a, b) => RANK[a.severity] - RANK[b.severity]);
}

export function PlatformOverviewScreen({
  destination, overview, events, loading, error, onRefresh, refreshing, onOpenAudit, onToggleProvider,
}: {
  destination: Destination;
  overview: PlatformOverviewDto | null;
  events: readonly TenantSummaryDto[];
  loading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  refreshing?: boolean;
  onOpenAudit?: () => void;
  /** TICK-026's manual kill switch (`superadminApi.toggleProvider`). Rejects when the switch failed. */
  onToggleProvider?: (id: ProviderId, enabled: boolean) => Promise<unknown>;
}) {
  const [togglingId, setTogglingId] = useState<ProviderId | null>(null);
  const [toggleError, setToggleError] = useState('');

  const toggleProvider = async (provider: ProviderStatusDto) => {
    if (!onToggleProvider) return;
    setToggleError('');
    setTogglingId(provider.id);
    try {
      await onToggleProvider(provider.id, provider.availability === 'disabled');
      onRefresh?.();
    } catch {
      setToggleError(`สลับสถานะ ${provider.name} ไม่สำเร็จ`);
    } finally {
      setTogglingId(null);
    }
  };

  const baht = (n: number) => `฿${Math.round(n).toLocaleString('th-TH')}`;
  const rail = decisions(overview);
  const active = events.filter((e) => e.status === 'active').length;

  const topEvents = [...events]
    .filter((e) => e.revenue > 0)
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 8);

  const slip = overview?.slipVerificationStats;
  const verifiedTotal = slip ? slip.totalVerified + slip.totalFailed : 0;

  return (
    <DashboardTemplate
      gate={{ destination, loading, error: error ?? null, onRetry: onRefresh }}
      kpis={
        <>
          <KpiCard label="รายได้รวมทั้งแพลตฟอร์ม" value={baht(overview?.totalRevenue ?? 0)} />
          <KpiCard
            label="อีเวนต์ที่เปิดอยู่"
            value={String(active)}
            meta={`ทั้งหมด ${overview?.eventCount ?? events.length}`}
          />
          <KpiCard label="คำสั่งซื้อ" value={(overview?.totalOrders ?? 0).toLocaleString('th-TH')} />
          <KpiCard
            label="สลิปค้างรอคนตรวจ"
            value={(overview?.slipVerificationDeadLetterCount ?? 0).toLocaleString('th-TH')}
            tone={(overview?.slipVerificationDeadLetterCount ?? 0) > 0 ? 'danger' : undefined}
            meta={verifiedTotal > 0 ? `ตรวจอัตโนมัติสำเร็จ ${slip!.totalVerified.toLocaleString('th-TH')} ใบ` : undefined}
          />
        </>
      }
      actions={
        <Card
          title="สิ่งที่ต้องตัดสินใจ"
          sub={rail.length === 0 ? undefined : 'เรียงตามผลกระทบถ้าไม่มีใครดู'}
          action={onRefresh && (
            <Button size="sm" variant="secondary" disabled={refreshing} onClick={onRefresh}>
              {refreshing ? 'กำลังรีเฟรช…' : 'รีเฟรช'}
            </Button>
          )}
        >
          {rail.length === 0 ? (
            <Alert tone="success" title="ไม่มีอะไรค้างอยู่">
              บริการหลักและผู้ให้บริการภายนอกตอบปกติ และไม่มีสลิปที่ตรวจอัตโนมัติไม่ผ่านค้างอยู่
            </Alert>
          ) : (
            <ul className="jt-decision-list">
              {rail.map((decision) => (
                <li key={decision.key} className="jt-decision">
                  <StatusBadge tone={SEVERITY_TONE[decision.severity]}>
                    {SEVERITY_LABEL[decision.severity]}
                  </StatusBadge>
                  <span className="jt-decision-copy">
                    <span className="jt-decision-title">{decision.title}</span>
                    <span className="jt-decision-detail">{decision.detail}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {onOpenAudit && (
            <div className="jt-inline-actions">
              <Button size="sm" variant="ghost" onClick={onOpenAudit}>เปิด Audit log ทั้งแพลตฟอร์ม</Button>
            </div>
          )}
        </Card>
      }
      chart={
        <Card title="รายได้รายอีเวนต์" sub="แปดอันดับแรกที่มีรายได้">
          {topEvents.length === 0 ? (
            <p className="jt-field-hint">ยังไม่มีอีเวนต์ที่มีรายได้</p>
          ) : (
            <BarChart
              label="รายได้รายอีเวนต์"
              data={topEvents.map((e) => ({ label: e.slug, value: e.revenue, display: baht(e.revenue) }))}
              caption="แกนตั้งเทียบกับอีเวนต์ที่รายได้สูงสุด"
            />
          )}
        </Card>
      }
      list={
        <div className="jt-registry-split--insight">
          <Card title="บริการหลัก" sub="ตรวจล่าสุดจากฝั่งเซิร์ฟเวอร์">
            <ul className="jt-checks">
              {(overview?.coreServices ?? []).map((service) => (
                <li key={service.name} className="jt-check" data-status={service.status === 'operational' ? 'passed' : 'failed'}>
                  <span className="jt-check-mark" aria-hidden="true">{service.status === 'operational' ? '✓' : '✕'}</span>
                  <span className="jt-check-copy">
                    <span className="jt-check-label">
                      {service.name}
                      <span className="jt-sr-only">
                        {' — '}
                        {service.status === 'operational' ? 'ปกติ' : service.status === 'degraded' ? 'ทำงานช้า' : 'ใช้งานไม่ได้'}
                      </span>
                    </span>
                    <span className="jt-check-detail">
                      {service.detail}
                      {service.latencyMs !== null && ` · ${service.latencyMs} ms`}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </Card>

          <Card title="ผู้ให้บริการภายนอก" sub="โควตาที่เหลือ และสิ่งที่จะหยุดทำงานถ้าหมด">
            {toggleError && <Alert tone="danger">{toggleError}</Alert>}
            {(overview?.providers ?? []).map((provider) => {
              const cap = provider.capacity;
              const remaining = cap?.remaining ?? null;
              const used = cap?.used ?? null;
              const unit = cap?.unit ?? (provider.id === 'thaibulksms' ? 'credits' : 'verifications');
              const unitLabel = unit === 'credits' ? 'เครดิต' : 'ครั้ง';
              const total = remaining !== null && used !== null ? remaining + used : null;
              const manuallyDisabled = provider.availability === 'disabled';
              // ThaiBulkSMS reports a balance but never a `used` figure, so it has no meter; its
              // remaining credit still has to be readable, and a balance it could not read is unknown.
              const showRemaining = !total && (cap !== null || (provider.id === 'thaibulksms' && !manuallyDisabled));
              return (
                <div key={provider.id} data-provider={provider.id}>
                  {total && total > 0 ? (
                    <MeterRow
                      label={provider.name}
                      value={`${remaining!.toLocaleString('th-TH')} ${unitLabel}`}
                      percent={(remaining! / total) * 100}
                      tone={remaining! / total < 0.2 ? 'warning' : 'positive'}
                      caption={provider.purpose}
                    />
                  ) : (
                    <div className="jt-inline-actions">
                      <StatusBadge tone={provider.availability === 'operational' ? 'success' : manuallyDisabled ? 'neutral' : 'warning'}>
                        {provider.name}
                      </StatusBadge>
                      <span className="jt-field-hint">
                        {provider.purpose} — {provider.detail || (manuallyDisabled ? 'ปิดใช้งานชั่วคราวโดยผู้ดูแลระบบ' : 'ไม่มีข้อมูลโควตา')}
                      </span>
                    </div>
                  )}
                  {showRemaining && (
                    <p className="jt-field-hint">
                      คงเหลือ <strong>{remaining !== null ? remaining.toLocaleString('th-TH') : 'ไม่ทราบ'}</strong> {unitLabel}
                    </p>
                  )}
                  {/*
                   * The rollout flag (`NOTIFICATIONS_*_ENABLED`) is environment configuration and is
                   * only reported here. The server folds the kill switch into `channelEnabled`, so
                   * while the provider is switched off by hand the flag cannot be read from it.
                   */}
                  {!manuallyDisabled && (provider.id === 'resend' || provider.id === 'thaibulksms') && provider.channelEnabled !== undefined && (
                    <p className="jt-field-hint">
                      {provider.channelEnabled
                        ? 'ช่องทางส่งเปิดใช้งาน'
                        : `การส่ง ${provider.id === 'resend' ? 'Email' : 'SMS'} ปิดอยู่ (rollout flag)`}
                    </p>
                  )}
                  {onToggleProvider && (
                    <div className="jt-inline-actions">
                      <Button
                        size="sm"
                        variant={manuallyDisabled ? 'secondary' : 'ghost'}
                        disabled={togglingId !== null}
                        aria-label={`${manuallyDisabled ? 'เปิดใช้งานผู้ให้บริการนี้' : 'ปิดใช้งานชั่วคราว'} — ${provider.name}`}
                        onClick={() => toggleProvider(provider)}
                      >
                        {togglingId === provider.id ? 'กำลังบันทึก…' : manuallyDisabled ? 'เปิดใช้งานผู้ให้บริการนี้' : 'ปิดใช้งานชั่วคราว'}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </Card>
        </div>
      }
    />
  );
}
